import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Image } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { fixUrl } from '../../../services/api';
import { escalonDe } from '../../../utils/depsEscalones';
import { NEUTROS as T } from '../../../utils/micrositioTema';
import { clickInterno } from '../../../utils/navegacionSpa';

// Cuántos DEPs entran en la tarjeta de la portada. Son diez en total y no
// entran: la ficha completa los muestra todos. El backend manda los diez en el
// orden estándar de la raza y el recorte es de layout, así que se decide acá.
// EXPORTADO: lo usa tambien la variante OP-2, que muestra los mismos cuatro.
// Si una plantilla mostrara mas DEPs que la otra, el comprador compararia dos
// toros con distinta cantidad de datos segun que plantilla eligio la cabaña.
export const DEPS_EN_PORTADA = 4;

// El hover vive en una hoja de estilos y no en `onMouseEnter`: con estado de
// React, cada entrada y salida del mouse re-renderiza la tarjeta entera —con
// sus imágenes— para mover un pixel y cambiar una sombra.
//
// SE EXPORTA PARA IZARLA. Estaba dibujada adentro del componente, o sea un
// `<style>` idéntico por cada toro del carrusel. Quien la monta la inyecta UNA
// vez a nivel de página, que es lo que ya hace `AnimalCatalogoPage` con la
// suya.
export const ESTILOS_BLOQUE_TORO = `
.pj-pieza {
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius}px;
  box-shadow: ${T.shadowSm};
  overflow: hidden;
  transition: box-shadow .15s, transform .15s;
}
.pj-pieza:hover { box-shadow: ${T.shadow}; transform: translateY(-1px); }
.pj-toro-grid {
  display: grid;
  grid-template-columns: minmax(0,1.1fr) minmax(0,1fr);
  gap: 16px;
  /* STRETCH Y NO START: con 'start' cada columna tomaba su altura natural y la
     foto terminaba antes que la ficha, dejando un escalon abajo a la izquierda.
     Con 'stretch' —el default de grid, explicito aca para que se lea— las dos
     columnas miden lo mismo: la derecha define el alto con su contenido y la
     foto lo copia.

     ES LA ALTURA REAL Y NO UN NUMERO A OJO. Antes habia un minHeight fijo
     buscando que coincidieran; un fijo no puede acertar, porque la columna
     derecha mide lo que midan la ficha, los DEPs y las secundarias de ESE
     animal. */
  align-items: stretch;
}
@media (max-width: 820px) { .pj-toro-grid { grid-template-columns: 1fr; } }

/* ── OP-2: la foto ES EL FONDO de la seccion, con la tarjeta encima ─────────
   Antes eran dos columnas —foto y tarjeta— y la tarjeta se montaba apenas por
   el borde. Ahora la foto ocupa TODO el bloque y la tarjeta flota sobre ella.
   La diferencia no es de grados: en dos columnas la foto es la mitad del
   argumento, de fondo es el argumento entero y los datos se leen sobre el
   animal.

   La foto va como background-image de CSS y NO como <Image> de antd, a
   proposito: en OP-2 la foto NO se amplia desde el index —una lupa sobre un
   fondo invita a un gesto que no lleva a ningun lado, y el que quiere ver al
   animal de cerca tiene la ficha a un click—. Prescindir del visor deja el
   encuadre a un background-position, que es por donde entra el punto focal por
   imagen.

   SIN COMILLAS INVERSAS en estos comentarios: viven dentro de un template
   literal de JS y la primera cierra la cadena. Van tres veces que pasa aca. */
.pj-op2-toro {
  position: relative;
  /* MAS ALTO QUE ANTES (460 -> 620) y sin radio ni margenes: el bloque ocupa el
     ancho completo de la pantalla. Y el alto importa para el ENCUADRE — con la
     foto recortada a 460px de alto sobre 1900 de ancho, cualquier animal que no
     este centrado verticalmente pierde la cabeza. Con mas alto se recorta
     menos. */
  min-height: 620px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  /* SIN padding lateral. El ancho util lo pone el contenedor interno, con el
     MISMO maxWidth y el MISMO padding que la cabecera de arriba: asi el titulo
     y la tarjeta caen sobre el mismo eje. Antes habia 40px aca contra 24 alla,
     y esos 16px de diferencia desalineaban las dos cosas que el comentario
     prometia alineadas.

     PADDING SIMETRICO, y no 140 arriba / 56 abajo como antes: con align-items
     center la tarjeta se centra en la caja de contenido, y una caja con 84px
     mas de aire arriba que abajo la deja corrida hacia abajo. El productor
     lo vio con una foto real (2026-09-29) y pidio la tarjeta centrada en la
     seccion. Los 140 de arriba reservaban lugar para la cabecera flotante,
     pero la cabecera cae a la IZQUIERDA y la tarjeta a la DERECHA: no se
     pisan, no hace falta reservar nada. */
  padding: 56px 0;
  overflow: hidden;
  background: ${T.pearl};
}

/* EL VELO, como capa propia y no como fondo de la cabecera. Antes el degradado
   era el background de .pj-op2-cabecera, asi que media exactamente lo que
   medía el titulo con su bajada: no habia forma de hacerlo mas alto o mas bajo
   sin mover el texto. Como capa aparte tiene su propio alto —que pone el
   COMPONENTE desde la config, op2_velo_alto— y su propio color, y se puede
   apagar sin tocar la cabecera.

   z-index 1: arriba de la foto (0) y debajo de la tarjeta (2) y de la
   cabecera (3), que es la que protege. Sin eventos: es pintura. */
.pj-op2-velo {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;
}

/* LA COLUMNA de la tarjeta: la tarjeta y, debajo, el boton "Ver todos los
   toros". El boton bajo de la cabecera a aca (2026-09-29): en OP-2 la cabecera
   flota arriba a la izquierda y la tarjeta abajo a la derecha, y el boton al
   lado del titulo quedaba a media pantalla de lo que invita a ampliar. Debajo
   de la tarjeta se lee como "hay mas de esto". Mide lo mismo que la tarjeta
   para que el boton se alinee con su borde. */
.pj-op2-columna {
  width: 100%;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* UN TORO SIN FOTO NO DIBUJA LA BANDA. En OP-1 el que no tiene imagen pierde la
   foto y la ficha sostiene el bloque igual; en OP-2 el diseño ES la foto, asi
   que sin ella quedaba una caja gris de 620px con la tarjeta flotando en el
   medio de la nada. Nada aparece si no hay dato detras. */
/* SIN FOTO NO HAY BANDA, pero SI se conserva el lugar de la cabecera.
   Al sacar TODO el padding, la tarjeta subia hasta el tope y quedaba debajo del
   velo con el titulo. Se mantiene el padding de arriba y se van el alto minimo,
   el fondo gris y el aire de abajo: no hay banda, pero la cabecera sigue
   teniendo donde apoyarse.

   Se resuelve en el BLOQUE y no en la seccion a proposito: el carrusel puede
   tener unos toros con foto y otros sin, y una clase a nivel seccion no sabria
   cual de los dos casos esta mostrando en cada slide. */
.pj-op2-toro.pj-op2-sin-foto {
  min-height: 0;
  padding: 140px 0 0;
  background: transparent;
}
.pj-op2-toro.pj-op2-sin-foto .pj-op2-columna {
  margin-left: auto;
  margin-right: auto;
  max-width: 560px;
}
.pj-op2-toro.pj-op2-sin-foto .pj-op2-card {
  max-width: none;
}

/* LA CABECERA DE OP-2, apoyada sobre la foto. Vive en CSS y no inline porque
   NO SIEMPRE FLOTA: cuando no hay foto detras o la pantalla es angosta vuelve
   al flujo, y esa decision se toma mejor con una media query que con JS. */
.pj-op2-cabecera {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding: 40px 0 64px;
  /* El velo no puede comerse el click del boton que vive protegiendo. */
  pointer-events: none;
}
.pj-op2-cabecera-interior {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  pointer-events: auto;
}

/* El ancho util, igual que el de la cabecera. */
.pj-op2-interior {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: 2;
}
.pj-op2-foto {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-size: cover;
  /* El position lo pone el COMPONENTE, foto por foto, desde imagen_foco: es el
     punto focal que eligio el productor. Este 'center' queda como respaldo para
     el caso en que no venga —una API vieja— y es el mismo 50/50 que trae la
     columna por default. (Sin comillas inversas: template literal.) */
  background-position: center;
  background-repeat: no-repeat;
}
.pj-op2-card {
  position: relative;
  z-index: 2;

  /* UN 20% MAS FINA que cuando era una columna del grid. Sobre la foto, una
     tarjeta ancha tapa al animal; angosta deja ver lo que se esta vendiendo. */
  width: 100%;
  max-width: 430px;
  background: ${T.surface};
  /* El borde se queda aunque ahora el fondo sea una foto: hay fotos claras
     —un toro blanco contra cielo— donde una tarjeta blanca sin borde se funde
     igual que se fundia contra la banda blanca. */
  border: 1px solid ${T.border};
  border-radius: ${T.radius}px;
  box-shadow: 0 12px 32px rgba(35,35,35,.24);
  padding: 24px;
}

/* EN PANTALLA ANGOSTA LA TARJETA NO FLOTA: se apoya abajo y ocupa el ancho.
   Una tarjeta de 430px sobre una foto de 360px de ancho la tapa entera, y ahi
   el bloque deja de mostrar al animal. */
@media (max-width: 900px) {
  /* EN MOBILE LA CABECERA TAMPOCO FLOTA. La foto baja a 280px y el titulo con
     el boton ocupa dos o tres renglones: como overlay quedaba encima del animal
     y la tarjeta, que sube con su margen negativo, terminaba debajo del velo. */
  .pj-op2-cabecera {
    position: static;
    padding: 0 0 24px;
  }
  .pj-op2-cabecera-interior { padding: 0; }
  /* Sin cabecera flotante no hay nada que proteger, y una franja de color
     sobre una foto de 280px se comeria al animal. */
  .pj-op2-velo { display: none; }
  .pj-op2-toro {
    min-height: 0;
    padding: 0;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }
  .pj-op2-foto { position: relative; inset: auto; min-height: 280px; }
  .pj-op2-columna { max-width: none; margin: -32px 12px 12px; align-items: stretch; }
  .pj-op2-card { max-width: none; }
}
`;


/**
 * La barra de percentil de UN DEP, en la tarjeta de la portada.
 *
 * OJO CON LA DIRECCIÓN, que es lo más caro de equivocar en esta pantalla: **1
 * es el mejor, 100 el peor**, y por eso la barra se llena con
 * `100 - percentil`. Pintarla proporcional al número mostraría a los mejores
 * toros como los peores, en la vidriera donde la cabaña vende su genética. El
 * razonamiento completo y la verificación contra datos de producción viven en
 * `utils/depsEscalones.js`, que es de donde sale la escala.
 *
 * MISMA ESCALA QUE LA FICHA, a propósito: el comprador ve el color en la
 * portada y lo vuelve a ver en la ficha del animal, y tiene que significar lo
 * mismo. Lo que diferencia a las dos pantallas es la CANTIDAD —acá entran
 * cuatro DEPs, la ficha muestra los diez—, no cómo se leen.
 */
// EXPORTADA para la variante OP-2. La barra de percentil es un INSTRUMENTO DE
// MEDICION, no decoracion: tiene que leerse igual en las dos plantillas, con la
// misma direccion y la misma escala. Duplicarla es la forma mas segura de que
// una de las dos se desincronice.
export const BarraDep = ({ dep, color, compacta = false }) => {
  // SIN PERCENTIL NO SE DIBUJA NADA, igual que en la ficha. Hoy no llega:
  // `_deps_publicos` (`micrositio_routes.py`) descarta los que no tienen rank.
  // Pero el modo de falla es el peor posible en esta pantalla: `100 - null` da
  // 100, o sea una barra LLENA —"el mejor de la raza"— y al lado un `top null%`
  // escrito, en la vidriera donde la cabaña vende. Dos consumidores del mismo
  // util manejando el null de dos formas distintas es exactamente la
  // divergencia que el util existe para evitar.
  if (dep.percentil == null) return null;

  const lleno = Math.max(0, Math.min(100, 100 - dep.percentil));
  const escalon = escalonDe(dep.percentil);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
      {/* `compacta` la usa OP-2, donde la tarjeta es un 20% mas fina porque
          flota sobre la foto. Sin esto la barra queda espachurrada entre la
          etiqueta y los dos numeros, y una barra de 30px no codifica nada: deja
          de ser un instrumento y pasa a ser un adorno.

          Se achican la ETIQUETA y la BARRA, no los numeros: el valor del DEP y
          el rank son el dato, y recortarlos los haria ilegibles. */}
      <span
        style={{
          fontSize: 11.5, color: T.textDim, flex: 'none',
          width: compacta ? 64 : 78,
          ...(compacta
            ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
            : {}),
        }}
        title={compacta ? dep.etiqueta : undefined}
      >
        {dep.etiqueta}
      </span>
      <div
        style={{
          flex: 1, height: 5, background: T.pearl, borderRadius: 3,
          overflow: 'hidden',
          // Un techo al ancho y no un ancho fijo: en la tarjeta angosta la
          // barra se queda corta y el aire sobrante queda para la etiqueta.
          ...(compacta ? { maxWidth: 96 } : {}),
        }}
      >
        <div
          style={{
            width: `${lleno}%`, height: '100%', borderRadius: 3,
            // Fuera de escalón se queda con el ACENTO DEL PRODUCTOR y no con
            // un gris: realzar necesita una línea de base de la que sobresalir.
            background: escalon ? escalon.color : color,
          }}
        />
      </div>
      {/* `!= null` para que un DEP de 0 —la línea base de la raza, el número
          más significativo de la evaluación— se muestre en vez de caerse. */}
      <span style={{ fontSize: 11.5, fontWeight: 700, width: 42, textAlign: 'right', flex: 'none' }}>
        {dep.valor != null ? dep.valor : '—'}
      </span>
      {/* EL PERCENTIL, ESCRITO. Antes en la portada no estaba en ningún lado:
          lo codificaba sólo el largo de la barra, y un comprador que ve la
          barra llena no sabe si es top 3% o top 28%. Va con el mismo
          tratamiento que en la ficha —mayúsculas y color dentro de escalón,
          gris y minúscula fuera— porque es el MISMO instrumento. */}
      <span
        style={{
          width: 52, flex: 'none', textAlign: 'right',
          fontSize: 10.5,
          ...(escalon
            ? {
                color: escalon.color, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '.03em',
              }
            : { color: T.textDim }),
        }}
        title={escalon ? `${escalon.etiqueta} — rank ${dep.percentil}% de la raza` : ''}
      >
        {/* "Rank" con mayúscula como en las fichas (2026-10-15). Tamaño y
            ancho quedan: esta es la barra compacta de la portada y los 52px
            del percentil son lo que entra al lado de cuatro barras. */}
        Rank {dep.percentil}%
      </span>
    </div>
  );
};


/**
 * El bloque destacado de UN toro.
 *
 * La foto y la ficha son DOS piezas separadas, cada una con sus cuatro puntas
 * redondeadas, y las secundarias van debajo de la ficha como una tercera. No es
 * una tarjeta partida en dos: son elementos que respiran, y cada uno levanta su
 * propia sombra al pasar el mouse.
 */
const BloqueToro = ({ animal, config, tipografia, slug }) => {
  const navigate = useNavigate();
  const acento = config.color_primary || '#1a5f2a';
  const colorTitulo = config.color_title || '#1a1a1a';
  const secundarias = animal.imagenes_secundarias || [];
  // LOS CUATRO MEJORES DE ESTE ANIMAL, no los cuatro primeros de la lista.
  //
  // Y la distinción es de PRODUCTO, no de código. Esta sección no es el
  // "barrido de catálogo" del que habla el cabañero —él se refiere a un
  // catálogo de REMATE, con 75 animales, donde comparar la misma fila hacia
  // abajo es todo—. Acá son diez toros como máximo: es la vidriera selecta, la
  // genética premium de la cabaña, y un artefacto para PRESUMIR.
  //
  // Con filas fijas la vidriera trabajaba en contra de la venta: al orden
  // estándar de la raza le toca abrir con Nacimiento, Destete, Peso final y
  // Leche y crecimiento, así que a PAYESERO lo mostraba con Destete en el
  // percentil 97 y Leche y crecimiento en 93 —dos de los peores de la raza— y
  // le tapaba AOB 20, %GI 19, Grasa dorsal 24 y Circunf. escrotal 25. O no
  // genera ganas de entrar, o peor: le baja el prestigio a un gran animal al
  // que lo termina de mostrar el conjunto de sus DEPs.
  //
  // Se pierde poder comparar la misma fila entre tarjetas, y está aceptado: en
  // una vidriera de diez eso no vale nada. Y no se esconde nada — los diez
  // DEPs, con sus percentiles, están a un click en la ficha.
  const deps = [...(animal.deps || [])]
    // Sin percentil no se puede ordenar ni dibujar; `BarraDep` igual se planta,
    // pero un `undefined` metido en el `sort` deja el orden indefinido.
    .filter((d) => d.percentil != null)
    // `1` es el MEJOR, así que ascendente. Y la copia con spread no es adorno:
    // `sort` MUTA, y mutar `animal.deps` sería escribir sobre las props.
    .sort((a, b) => a.percentil - b.percentil)
    .slice(0, DEPS_EN_PORTADA);

  const hayDescripcion = Boolean((animal.descripcion || '').trim());

  // EL "VER FICHA" SE DECLARA UNA VEZ Y SE DIBUJA EN DOS, a proposito: en el pie
  // de la ficha SIEMPRE, y otra vez al final del bloque de la descripcion cuando
  // hay descripcion. NO es un duplicado por descuido — el que termina de leer la
  // descripcion ya decidio, y no tiene por que volver a subir a buscar el boton.
  //
  // ESTUVO AL REVES, con `{!hayDescripcion && verFicha}` en el pie, y por eso se
  // deja escrito: si alguien ve los dos botones, lo lee como un bug y "arregla"
  // volviendo a esa condicion, el toro CON descripcion pierde el camino del pie
  // en silencio — sin error y sin test que falle.
  //
  // Apuntaba a esta ruta cuando todavia no existia —decision explicita del
  // usuario, para cerrar la estetica— y justamente por apuntar a la ruta REAL y
  // no a un `#`, el dia que se construyo la ficha empezo a funcionar sin tocar
  // nada.
  const verFicha = (
    <a
      href={`/genetica/${slug}/animal/${animal.slug}`}
      // Se intercepta el click simple para RUTEAR en vez de recargar la app
      // entera; el `href` sigue sirviendo a ctrl+click, al boton del medio y a
      // los buscadores. Ver `navegacionSpa.js`.
      onClick={clickInterno(navigate, `/genetica/${slug}/animal/${animal.slug}`)}
      style={{
        flex: 'none', fontSize: 12.5, fontWeight: 700, color: acento,
        display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      }}
    >
      Ver ficha <RightOutlined style={{ fontSize: 10 }} />
    </a>
  );

  return (
    <>
      {/* TODAS las fotos del animal en UN grupo: al abrir una se puede pasar a
          las otras sin cerrar y volver a entrar. Un comprador que está mirando
          un toro quiere ver las tres seguidas, no una por vez. */}
      <Image.PreviewGroup>
      <div className="pj-toro-grid">
        {/* ── Pieza 1: la foto. Pesa más que los datos porque es lo que vende. */}
        {/* EL ALTO LO PONE EL GRID, no este numero. `align-items: stretch`
            estira esta pieza hasta el alto de la columna de al lado, asi que la
            foto termina exactamente donde terminan las secundarias.

            El `minHeight` queda como PISO para el caso corto —un animal sin
            DEPs y sin secundarias deja la columna derecha muy baja, y una foto
            de 120px de alto no vende nada—. No es el que manda: manda el grid. */}
        <div className="pj-pieza" style={{ background: T.pearl, minHeight: 344 }}>
          {animal.imagen ? (
            <Image
              src={fixUrl(animal.imagen)}
              alt={animal.nom_animal || 'Toro'}
              loading="lazy"
              // `width` en el wrapper y `height` en el img: antd envuelve la
              // imagen en un span, y sin esto el contenedor colapsa a la altura
              // del ícono de lupa.
              wrapperStyle={{ width: '100%', height: '100%', display: 'block' }}
              // SIN `minHeight` aca: con el puesto, la imagen no podia ser mas
              // baja que 344 aunque el grid le diera menos, y tampoco seguia al
              // contenedor cuando le daba mas. El `height: 100%` sigue al div,
              // y el piso vive una sola vez, en el div.
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              preview={{ mask: <span style={{ fontSize: 13 }}>Ver en grande</span> }}
            />
          ) : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ── Pieza 2: la ficha ──────────────────────────────────────── */}
          <div className="pj-pieza" style={{ padding: '18px 18px 0' }}>
            <div
              style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 16.5, fontWeight: 800, letterSpacing: '-.005em',
                    lineHeight: 1.25, fontFamily: tipografia.title, color: colorTitulo,
                  }}
                >
                  {/* El nombre CORTO manda: es con el que se conoce al animal.
                      El backend ya cae a `nom_animal` si el productor no lo
                      cargó, así que acá no hace falta el fallback. */}
                  {animal.nombre_corto || animal.nom_animal || animal.rp_animal || 'Sin nombre'}
                </div>
                {/* El de pedigrí completo queda abajo, chico. No es relleno: es
                    el nombre con el que el animal figura en la asociación, y un
                    comprador que va a cruzar datos lo necesita. Sólo se dibuja
                    si es DISTINTO del corto — repetirlo sería ruido. */}
                {animal.nombre_corto && animal.nom_animal !== animal.nombre_corto && (
                  <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 2 }}>
                    {animal.nom_animal}
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 3, fontWeight: 500 }}>
                  {[
                    animal.nom_categoria,
                    animal.rp_animal ? `RP ${animal.rp_animal}` : null,
                    animal.cabana,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              {animal.nom_raza && (
                /* Misma forma que el chip de raza de la ficha y que el de
                   rank: radio 4, sin borde, fondo tenue del acento. SIN
                   mayusculas — esas quedan solo para el rank. */
                <span
                  style={{
                    flex: 'none', fontSize: 11.5, fontWeight: 700, letterSpacing: '.03em',
                    background: `${acento}14`, color: acento, borderRadius: 4,
                    padding: '2px 8px', whiteSpace: 'nowrap',
                  }}
                >
                  {animal.nom_raza}
                </span>
              )}
            </div>

            {deps.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em',
                    textTransform: 'uppercase', color: T.textDim, marginBottom: 8,
                  }}
                >
                  DEPs · percentil en la raza
                </div>
                {deps.map((dep) => (
                  <BarraDep key={dep.clave} dep={dep} color={acento} />
                ))}
              </div>
            )}

            {/* ── Pie de la ficha ────────────────────────────────────── */}
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, marginTop: 16, padding: '12px 0 16px',
                borderTop: `1px solid ${T.border}`,
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textDim }}>
                {animal.apto_comercializar ? (
                  <span style={{ color: T.success }}>● Disponible</span>
                ) : (
                  'En el plantel'
                )}
              </span>
              {/* ESTE BOTON VA SIEMPRE. El de abajo, en el bloque de la
                  descripcion, es un SEGUNDO camino a la ficha y no un
                  reemplazo: el toro sin descripcion no dibuja aquel bloque y
                  se quedaria sin ninguno. */}
              {verFicha}
            </div>
          </div>

          {/* ── Pieza 3: las secundarias, DEBAJO de la ficha ───────────── */}
          {secundarias.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${secundarias.length}, minmax(0,1fr))`,
                gap: 16,
              }}
            >
              {secundarias.map((img) => (
                <div
                  key={img.url}
                  className="pj-pieza"
                  style={{ height: 128, background: T.pearl, position: 'relative' }}
                >
                  <Image
                    src={fixUrl(img.url)}
                    alt=""
                    loading="lazy"
                    wrapperStyle={{ width: '100%', height: '100%', display: 'block' }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    preview={{ mask: <span style={{ fontSize: 12 }}>Ver</span> }}
                  />
                  {/* Se dice cuándo la foto es de un familiar y no del animal.
                      Una foto del padre mostrada como si fuera del toro es una
                      mentira, y el comprador que la descubre deja de creerle al
                      resto de la ficha. */}
                  {img.origen === 'familiar' && (
                    <span
                      style={{
                        position: 'absolute', left: 8, bottom: 8,
                        fontSize: 10, fontWeight: 700, letterSpacing: '.02em',
                        background: 'rgba(255,255,255,.92)', color: T.textDim,
                        borderRadius: 999, padding: '3px 8px',
                      }}
                    >
                      Familiar
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── La descripcion, DEBAJO del bloque entero ──────────────────────
          A TODO EL ANCHO y fuera de `pj-toro-grid`, no adentro de la ficha:
          es el parrafo que presenta al animal, y leerlo en la columna angosta
          de los datos lo convertia en un dato mas. Aca cierra el bloque.

          QUEDAN DOS CAMINOS A LA FICHA —el del pie de la ficha y este— y es a
          proposito: el comprador que termina de leer la descripcion ya decidio,
          y no tiene por que volver a subir a buscar el boton.

          Los dots del carrusel caen DEBAJO de esto: van `position: absolute`
          con `bottom` negativo respecto del carrusel, asi que crecer el slide
          los empuja hacia abajo solo. Por eso el margen de abajo.

          MISMA FAMILIA que el bloque de la ficha (`AnimalCatalogoPage`) —barra
          del acento, borde tenue, tokens de `micrositioTema`— pero NO identico,
          y la diferencia es a proposito: aca la barra va de 4px y alla de 3,
          porque este bloque es mas grande y cierra un slide entero. Se dice
          cual es la diferencia en vez de afirmar que son iguales: un comentario
          que promete "misma forma" hace que el proximo cambie uno y deje el
          otro atras sin enterarse.

          La paleta es la del productor (`config.color_primary`), no la del
          artifact de donde salio la forma. */}
      {hayDescripcion && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 20,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderLeft: `4px solid ${acento}`,
            borderRadius: T.radius,
            boxShadow: T.shadowSm,
            padding: '18px 22px',
            marginTop: 16,
            marginBottom: 8,
          }}
        >
          <p
            style={{
              margin: 0, flex: 1, minWidth: 0,
              fontFamily: tipografia.body, fontSize: 15, lineHeight: 1.6,
              // '#666' y no T.textDim: es la convencion vigente del micrositio
              // —3 veces en AnimalCatalogoPage, 1 en CatalogoPage— y T.textDim
              // es '#6b6b6b', un gris DISTINTO. Migrar los cinco a T.textDim es
              // un trabajo aparte; dejar uno solo migrado es la divergencia que
              // micrositioTema.js vino a cerrar.
              color: config.color_paragraph || '#666',
              // RECORTADA A TRES LINEAS. Es texto libre: nada impide que
              // alguien escriba diez, y en una portada eso descuadra la grilla
              // entera. El recorte le da al boton una razon REAL de existir:
              // hay mas texto del que se ve.
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {animal.descripcion}
          </p>
          {verFicha}
        </div>
      )}
      </Image.PreviewGroup>
    </>
  );
};

export default BloqueToro;
